import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { ChevronLeft, ChevronRight, Check, Package, Link2, Settings, Zap, Users, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { productAPI, projectAPI } from '../../services/api';
import { isValidProductId, resolveProductId } from '@/lib/productId';
import {
  buildProductCreatePayload,
  createProveRequestDraft,
  validateProveRequestStage,
} from '@/lib/proveRequestForm';
import {
  TailAdminCodeBlock,
  TailAdminFormSection,
  TailAdminPageHeader,
  TailAdminStatusBadge,
} from '@/components/template/tailadmin/DashboardPrimitives';

const steps = [
  { id: 1, title: 'Customer route', description: 'One-to-one or Broadcast', icon: Users },
  { id: 2, title: 'Campaign details', description: 'What customers will see', icon: Package },
  { id: 3, title: 'Reward', description: 'Verified subscription reward', icon: Zap },
  { id: 4, title: 'Subscription check', description: 'Secure connection', icon: Settings },
  { id: 5, title: 'Sharing', description: 'How Agents can promote', icon: Link2 }
];

const fieldClassName = "mt-1 border-gray-200 focus:border-brand-400";
const helperClassName = "mt-1 text-xs leading-5 text-gray-500";
const errorClassName = "mt-1 text-sm text-red-600";

const ProveRequestCreate = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState(() => createProveRequestDraft({ projectMode: 'existing' }));
  const [projects, setProjects] = useState([]);

  const [errors, setErrors] = useState({});
  const [developerOpen, setDeveloperOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    projectAPI.getProjects()
      .then((response) => {
        if (!cancelled) {
          const nextProjects = Array.isArray(response.data) ? response.data : [];
          setProjects(nextProjects);
          if (nextProjects.length === 0) {
            setFormData((previous) => ({ ...previous, projectMode: 'create', projectId: '' }));
          }
        }
      })
      .catch(() => {
        if (!cancelled) setProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const validateStep = (step) => {
    const stages = { 1: 'request', 2: 'offer', 3: 'economics', 4: 'integration' };
    const newErrors = stages[step]
      ? validateProveRequestStage(formData, stages[step])
      : {};

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setLoading(true);
    try {
      let submittedForm = formData;
      if (formData.projectMode === 'create') {
        const projectResponse = await projectAPI.createProject({
          name: formData.projectName.trim(),
          description: formData.projectDescription?.trim() || null,
          idempotencyKey: formData.projectIdempotencyKey,
        });
        const project = projectResponse.data || {};
        const projectId = project.project_id || project.projectId;
        if (!projectId) throw new Error('Project creation did not return a Project ID');
        submittedForm = { ...formData, projectId, projectMode: 'existing' };
        setFormData(submittedForm);
      }
      const payload = buildProductCreatePayload(submittedForm);

      const response = await productAPI.createProduct(payload);
      toast.success('Campaign created successfully!');

      const data = response.data;
      const product = data.product || data;
      const resolvedProductId = resolveProductId(product);
      const campaignId = data.default_prove_request?.campaign_id || product.defaultCampaignId;

      if (formData.referralEnabled) {
        if (!isValidProductId(resolvedProductId)) {
          toast.error('Product created but setup link is unavailable');
          navigate('/client/products');
          return;
        }
        navigate(`/client/prove-requests/${resolvedProductId}/setup`);
      } else if (campaignId) {
        navigate(`/client/campaigns/${campaignId}`);
      } else {
        navigate('/client/products');
      }
    } catch (error) {
      console.error('Error creating prove-request:', error);
      toast.error(error.response?.data?.detail || 'Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-5">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">How will Agents introduce customers?</h3>
              <p className={helperClassName}>Choose one-to-one for a personal introduction or Broadcast for a tracked link shared with many people.</p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <button
                type="button"
                aria-pressed={formData.requestType === 'direct'}
                onClick={() => setFormData({
                  ...formData,
                  requestType: 'direct',
                  integrationMethod: 'api',
                  referralEnabled: false,
                  audiencePayEnabled: false,
                })}
                className={`rounded-2xl border p-5 text-left transition ${
                  formData.requestType === 'direct'
                    ? 'border-brand-400 bg-brand-50 ring-2 ring-brand-100'
                    : 'border-gray-200 bg-white hover:border-brand-200'
                }`}
              >
                <span className="text-base font-semibold text-gray-900">Meet customers one at a time</span>
                <span className="mt-2 block text-sm leading-6 text-gray-500">
                  One Agent personally introduces each customer.
                </span>
              </button>

              <button
                type="button"
                aria-pressed={formData.requestType === 'broadcast'}
                onClick={() => setFormData({
                  ...formData,
                  requestType: 'broadcast',
                  directVersion: '',
                  integrationMethod: 'webhook',
                  referralEnabled: true,
                })}
                className={`rounded-2xl border p-5 text-left transition ${
                  formData.requestType === 'broadcast'
                    ? 'border-brand-400 bg-brand-50 ring-2 ring-brand-100'
                    : 'border-gray-200 bg-white hover:border-brand-200'
                }`}
              >
                <span className="flex items-center gap-2 text-base font-semibold text-gray-900">
                  <Megaphone className="h-4 w-4" /> Share a tracked link
                </span>
                <span className="mt-2 block text-sm leading-6 text-gray-500">
                  Broadcast remains a one-to-many campaign with attributable share links.
                </span>
              </button>
            </div>

            {formData.requestType === 'direct' && (
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <h4 className="text-sm font-semibold text-gray-900">Where are customer details entered?</h4>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {[
                    ['v1', 'Customer signs up on your site', 'The Agent records the introduction and shares your signup link. Proovam verifies the subscription. Simple signup · V1'],
                    ['v2', 'Agent uses your customer form', 'The Agent submits your approved questions and your system returns a customer reference. Guided form · V2'],
                  ].map(([value, title, description]) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={formData.directVersion === value}
                      onClick={() => setFormData({ ...formData, directVersion: value })}
                      className={`rounded-xl border bg-white p-4 text-left ${
                        formData.directVersion === value ? 'border-brand-400 ring-2 ring-brand-100' : 'border-gray-200'
                      }`}
                    >
                      <span className="block text-sm font-semibold text-gray-900">{title}</span>
                      <span className="mt-1 block text-xs leading-5 text-gray-500">{description}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(errors.requestType || errors.directVersion) && (
              <p className={errorClassName}>{errors.requestType || errors.directVersion}</p>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="rounded-xl border border-brand-200 bg-brand-50 p-4">
              {projects.length > 0 && (
                <div className="mb-4 grid gap-2 sm:grid-cols-2">
                  <Button type="button" variant={formData.projectMode === 'existing' ? 'default' : 'outline'} onClick={() => setFormData({ ...formData, projectMode: 'existing' })}>Use existing Project</Button>
                  <Button type="button" variant={formData.projectMode === 'create' ? 'default' : 'outline'} onClick={() => setFormData({ ...formData, projectMode: 'create', projectId: '' })}>Create new Project</Button>
                </div>
              )}
              {formData.projectMode === 'existing' ? (
                <>
                  <Label htmlFor="project-id">Project *</Label>
                  <Select value={formData.projectId} onValueChange={(value) => setFormData({ ...formData, projectId: value })}>
                    <SelectTrigger id="project-id" className={fieldClassName}><SelectValue placeholder="Choose a Project" /></SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => {
                        const id = project.project_id || project.projectId;
                        return <SelectItem key={id} value={id}>{project.name}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                  {errors.projectId && <p className={errorClassName}>{errors.projectId}</p>}
                </>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="project-name">Project name *</Label>
                    <Input id="project-name" value={formData.projectName} onChange={(event) => setFormData({ ...formData, projectName: event.target.value })} placeholder="e.g. Consumer subscriptions" className={fieldClassName} />
                    {errors.projectName && <p className={errorClassName}>{errors.projectName}</p>}
                  </div>
                  <div>
                    <Label htmlFor="project-description">Project description</Label>
                    <Textarea id="project-description" value={formData.projectDescription} onChange={(event) => setFormData({ ...formData, projectDescription: event.target.value })} rows={2} className={fieldClassName} />
                  </div>
                </div>
              )}
              <p className={helperClassName}>This Project will keep the Offer, its campaigns, budgets, and reporting together.</p>
            </div>
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-gray-700">Campaign name *</Label>
              <Input
                id="name"
                placeholder="e.g., FinanceApp Pro Subscription"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={fieldClassName}
              />
              {errors.name && <p className={errorClassName}>{errors.name}</p>}
            </div>

            <div>
              <Label htmlFor="description" className="text-sm font-medium text-gray-700">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe what Agents will share with customers..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className={fieldClassName}
              />
            </div>

            <div>
              <Label htmlFor="productUrl" className="text-sm font-medium text-gray-700">Customer signup link *</Label>
              <Input
                id="productUrl"
                placeholder="https://yourapp.com"
                value={formData.productUrl}
                onChange={(e) => setFormData({ ...formData, productUrl: e.target.value })}
                className={fieldClassName}
              />
              <p className={helperClassName}>The page customers visit to start subscribing.</p>
              {errors.productUrl && <p className={errorClassName}>{errors.productUrl}</p>}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="goalType" className="text-sm font-medium text-gray-700">Verified result</Label>
              <Input id="goalType" value="Subscription" readOnly className={`${fieldClassName} bg-gray-50`} />
              <p className={helperClassName}>This release verifies subscriptions only.</p>
            </div>

            <div>
              <Label htmlFor="subscriptionAmount" className="text-sm font-medium text-gray-700">Subscription Amount (₦) *</Label>
              <Input
                id="subscriptionAmount"
                type="number"
                value={formData.subscriptionAmount}
                onChange={(e) => setFormData({ ...formData, subscriptionAmount: e.target.value })}
                className={fieldClassName}
                min="0"
              />
              <p className={helperClassName}>The price the customer pays; this is separate from Agent commission.</p>
              {errors.subscriptionAmount && <p className={errorClassName}>{errors.subscriptionAmount}</p>}
            </div>

            <div>
              <Label htmlFor="commissionPerGoal" className="text-sm font-medium text-gray-700">Agent reward per verified subscription (₦) *</Label>
              <Input
                id="commissionPerGoal"
                type="number"
                placeholder="5000"
                value={formData.commissionPerGoal}
                onChange={(e) => setFormData({ ...formData, commissionPerGoal: e.target.value })}
                className={fieldClassName}
                min="0"
                step="0.01"
              />
              <p className={helperClassName}>How much an Agent earns after a subscription is verified.</p>
              {errors.commissionPerGoal && <p className={errorClassName}>{errors.commissionPerGoal}</p>}
            </div>

            <p className={helperClassName}>
              Platform fee is set by Proovam admin — you only configure the Agent reward per verified subscription.
            </p>

            {formData.requestType === 'broadcast' && (
              <div className="rounded-2xl border border-brand-200 bg-brand-50 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Pay for audience distribution</h4>
                    <p className={helperClassName}>Add a negotiated media fee alongside performance commission.</p>
                  </div>
                  <Switch
                    checked={formData.audiencePayEnabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, audiencePayEnabled: checked })}
                  />
                </div>
                {formData.audiencePayEnabled && (
                  <div className="mt-4">
                    <Label htmlFor="audiencePayModel">Audience pay model</Label>
                    <Select value={formData.audiencePayModel} onValueChange={(value) => setFormData({ ...formData, audiencePayModel: value })}>
                      <SelectTrigger id="audiencePayModel" className={fieldClassName}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pure_performance">Pure performance</SelectItem>
                        <SelectItem value="performance_triggered_media_fee">Performance-triggered media fee</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="userIdentifierType" className="text-sm font-medium text-gray-700">How customers are matched</Label>
              <Select value={formData.userIdentifierType} onValueChange={(value) => setFormData({ ...formData, userIdentifierType: value })}>
                <SelectTrigger className={fieldClassName}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="phone">Phone Number</SelectItem>
                  <SelectItem value="email">Email Address</SelectItem>
                  <SelectItem value="custom">Custom ID</SelectItem>
                </SelectContent>
              </Select>
              <p className={helperClassName}>Choose the customer detail your system uses to prevent duplicate rewards.</p>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div className="rounded-2xl border border-brand-200 bg-brand-50 p-4 text-sm leading-6 text-gray-700">
              Your developer can add the secure system addresses used to create customers and verify subscriptions. These are separate from the customer signup link.
            </div>
            <details
              open={developerOpen || Boolean(errors.registrationUrl || errors.verificationUrl)}
              onToggle={(event) => setDeveloperOpen(event.currentTarget.open)}
              className="rounded-2xl border border-gray-200 bg-white p-4"
            >
              <summary className="cursor-pointer font-semibold text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2">For your developer: connection addresses</summary>
              <div className="mt-4 space-y-4">
            {formData.requestType === 'direct' && formData.directVersion === 'v2' && (
              <div>
                <Label htmlFor="registrationUrl" className="text-sm font-medium text-gray-700">Registration API URL *</Label>
                <Input
                  id="registrationUrl"
                  placeholder="https://yourapp.com/api/proovam/register"
                  value={formData.registrationUrl}
                  onChange={(e) => setFormData({ ...formData, registrationUrl: e.target.value })}
                  className={fieldClassName}
                />
                <p className={helperClassName}>Must return a stable external customer reference. Proovam never collects passwords.</p>
                {errors.registrationUrl && <p className={errorClassName}>{errors.registrationUrl}</p>}
              </div>
            )}
            <div>
              <Label htmlFor="verificationUrl" className="text-sm font-medium text-gray-700">
                {formData.requestType === 'broadcast' ? 'Webhook address for subscription updates *' : 'Secure API URL for checking subscription records *'}
              </Label>
              <Input
                id="verificationUrl"
                placeholder={formData.requestType === 'broadcast' ? 'https://yourapp.com/api/proovam/conversion' : 'https://yourapp.com/api/proovam/verify'}
                value={formData.verificationUrl}
                onChange={(e) => setFormData({ ...formData, verificationUrl: e.target.value })}
                className={fieldClassName}
              />
              <p className={helperClassName}>
                {formData.requestType === 'broadcast'
                  ? 'Ask your developer for the HTTPS address that receives subscription updates. Tracked links remain the attribution evidence.'
                  : 'Ask your developer for the HTTPS API address Proovam can use to look up subscription status. This is not the customer signup link.'}
              </p>
              {errors.verificationUrl && <p className={errorClassName}>{errors.verificationUrl}</p>}
            </div>

            {formData.requestType === 'direct' && <div className="rounded-2xl border border-brand-200 bg-brand-50 p-4">
              <h4 className="mb-2 text-sm font-semibold text-gray-900">Expected API Response</h4>
              <TailAdminCodeBlock className="bg-white text-gray-800">
{`GET ${formData.verificationUrl || "https://yourapp.com/api/proovam/verify"}?${formData.userIdentifierType}=VALUE

Response:
{
  "subscribed": true,
  "subscription_date": "2025-01-15"
}`}
              </TailAdminCodeBlock>
            </div>}
              </div>
            </details>
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <div className="rounded-2xl border border-brand-200 bg-brand-50 p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-brand-500 shadow-theme-xs">
                  <Link2 className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {formData.requestType === 'broadcast' ? 'Broadcast distribution' : 'Optional referral promotion'}
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-gray-500">
                    {formData.requestType === 'broadcast'
                      ? 'Broadcast requests always use attributable share links.'
                      : 'Allow Agents to also promote your product remotely via shareable links.'}
                  </p>
                </div>
                <Switch
                  checked={formData.referralEnabled}
                  disabled={formData.requestType === 'broadcast'}
                  onCheckedChange={(checked) => setFormData({ ...formData, referralEnabled: checked })}
                />
              </div>
              {formData.referralEnabled && (
                <div className="mt-5 space-y-4 border-t border-brand-200 pt-5">
                  <div>
                    <h4 className="mb-2 text-sm font-semibold text-gray-900">Benefits</h4>
                    <ul className="grid gap-2 text-sm text-gray-600 sm:grid-cols-2">
                      <li className="rounded-xl bg-white px-3 py-2 shadow-theme-xs">Wider reach across social, WhatsApp, and email</li>
                      <li className="rounded-xl bg-white px-3 py-2 shadow-theme-xs">Track every click and conversion</li>
                      <li className="rounded-xl bg-white px-3 py-2 shadow-theme-xs">Keep the same commission structure</li>
                      <li className="rounded-xl bg-white px-3 py-2 shadow-theme-xs">Let BRMs work from anywhere</li>
                    </ul>
                  </div>

                  <div>
                    <Label htmlFor="productType" className="text-sm font-medium text-gray-700">Product Type</Label>
                    <Select value={formData.productType} onValueChange={(value) => setFormData({ ...formData, productType: value })}>
                      <SelectTrigger className={fieldClassName}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="web_app">
                          <div>
                            <div className="font-medium">Web Application</div>
                            <div className="text-xs text-gray-500">Website or web app (JS tracker or webhook)</div>
                          </div>
                        </SelectItem>
                        <SelectItem value="mobile_app">
                          <div>
                            <div className="font-medium">Native Mobile App</div>
                            <div className="text-xs text-gray-500">iOS/Android app (webhook only)</div>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                    <p className="text-sm leading-6 text-amber-700">
                      <strong>Integration Required:</strong> You'll need to add tracking code to your {formData.productType === 'mobile_app' ? 'app' : 'website'} in the next step.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {!formData.referralEnabled && (
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-sm leading-6 text-gray-600">
                  <strong>Direct acquisition:</strong> Agents will register people they deliberately acquire. Referral promotion can be enabled later.
                </p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-6 sm:px-6">
      <TailAdminPageHeader
        eyebrow="Client workspace"
        title="Create campaign"
        description="Choose one-to-one or Broadcast, set the verified subscription reward, and connect subscription confirmation."
        actions={
          <Button variant="outline" onClick={() => navigate('/client/products')}>
            Cancel
          </Button>
        }
      />

      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs sm:p-5">
        <div className="grid gap-3 md:grid-cols-5">
          {steps.map((step) => {
            const Icon = step.icon;
            const complete = currentStep > step.id;
            const active = currentStep === step.id;

            return (
              <button
                key={step.id}
                type="button"
                onClick={() => {
                  if (step.id < currentStep || validateStep(currentStep)) {
                    setCurrentStep(step.id);
                  }
                }}
                className={`flex items-center gap-3 rounded-xl border p-3 text-left transition ${
                  active
                    ? 'border-brand-200 bg-brand-50'
                    : complete
                      ? 'border-emerald-200 bg-emerald-50'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                    complete || active ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {complete ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-gray-900">{step.title}</span>
                  <span className="block truncate text-xs text-gray-500">{step.description}</span>
                </span>
              </button>
            );
          })}
        </div>
        <div className="mt-5 h-2 overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full rounded-full bg-brand-500 transition-all"
            style={{ width: `${(currentStep / steps.length) * 100}%` }}
          />
        </div>
      </section>

      <TailAdminFormSection
        icon={steps[currentStep - 1].icon}
        title={steps[currentStep - 1].title}
        description={steps[currentStep - 1].description}
        className="max-w-3xl"
      >
        <div className="mb-5 flex items-center gap-2">
          <TailAdminStatusBadge tone="primary">
            Step {currentStep} of {steps.length}
          </TailAdminStatusBadge>
        </div>
        {renderStepContent()}
      </TailAdminFormSection>

      <div className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={currentStep === 1}
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          Previous
        </Button>

        {currentStep < steps.length ? (
          <Button
            onClick={nextStep}
            disabled={currentStep === 1 && (!formData.requestType || (formData.requestType === 'direct' && !formData.directVersion))}
          >
            Next
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Creating...' : 'Create campaign'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProveRequestCreate;
